import { apiClient } from "@/api/api-client";
import FloatingAction from "@/components/core/actions/floating-action";
import Badge from "@/components/core/badge";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import TagsInput from "@/components/core/forms/tags-input";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { userPermissions } from "@/constants/user-permissions";
import { caseRecordsQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { caseTypesQueryOptions } from "@/features/dashboard/api/queries/case-type-queries";
import { HasPermission } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCalendar, IconFileDownload, IconMoodLookDown, IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const columns: ColumnDef<CaseRecord>[] = [
	{
		id: "case_number",
		label: "Case No. / Case Title",
		cellClassName: "min-w-[150px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data, onClick }) => (
			<Link to={`/case-evaluation/${data.id}`} className="flex flex-col font-semibold ">
				<span className="text-blue-600">{data.case_number}</span>
				<span className="text-foreground-dark">{data.case_title}</span>
			</Link>
		),
	},
	/*{
		id: "case_title",
		label: "Case Title",
		cellClassName: "",
	headerClassName: "whitespace-nowrap",
		cell: ({ data }) => data.case_title,
	},*/
	{
		id: "case_category",
		label: "Case Category",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span>{data.case_category}</span>,
	},
	{
		id: "case_type",
		label: "Case Type",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span>{data.case_type}</span>,
	},
	{
		id: "filed_at",
		label: "Date Filed",
		cellClassName: "text-right whitespace-nowrap",
		headerClassName: "whitespace-nowrap text-right",
		cell: ({ data }) => formatDate(data.filed_at, "PP"),
	},
];

export const Route = createFileRoute("/_backoffice/case-evaluation")({
	validateSearch: z.object({
		status: z.string().optional(),
		search: z.string().optional(),
		per_page: z.number().optional(),
		stage: z.string().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		status: search.status,
		stage: search.stage || "all",
		search: search.search,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseRecordsQueryOptions({
				...deps,
			}),
		);
	},
	component: Page,
});

function Page() {
	const navigate = useNavigate({
		from: "/case-evaluation",
	});
	const search = Route.useSearch();
	const caseQuery = useSuspenseQuery(
		caseRecordsQueryOptions({
			...search,
			stage: search.stage || "all",
		}),
	);

	const cases = caseQuery.data.data;
	const meta = caseQuery.data.meta;

	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStages = caseStageQuery.data.data;

	const modal = useModal();
	const [isGenerating, setIsGenerating] = useState(false);

	const handleOpenReportModal = () => {
		modal.openModal({
			title: "Generate Case Record List Report",
			size: "md",
			component: <ReportModal />,
		});
	};
	return (
		<Content pageName="Cases">
			{/*<Header title="Cases" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Cases</span>
						<span className="text-base text-foreground/70">Track and manage case progress across different stages</span>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" onClick={handleOpenReportModal}>
							<IconFileDownload size={18} stroke={2} />
							<span>Generate Report</span>
						</Button>
						<HasPermission permissions={[userPermissions.ADD_CASE_FILING]}>
							<Link to="/case-evaluation/create">
								<Button>
									<IconPlus size={18} stroke={2} />
									<span className="">Add New</span>
								</Button>
							</Link>
						</HasPermission>
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					<Tabs
						defaultValue={search.stage || "all"}
						onValueChange={async (value) => {
							await navigate({
								to: "/case-evaluation",
								search: (old) => ({
									...old,
									stage: value,
								}),
							});
						}}
					>
						<TabsList>
							<TabsTrigger value={"all"} count={meta.all?.count}>
								All
							</TabsTrigger>
							<TabsTrigger value={"branch_receiving"} count={meta.count_per_status?.branch_receiving}>
								For Receiving
							</TabsTrigger>
							{caseStages
								.filter((stage) => stage.is_visible === true && stage.is_judgment !== true)
								.map((stage, index) => {
									// Insert "Under Mediation" before "Trial Proper"
									if (stage.label === "Trial Proper") {
										return (
											<>
												<TabsTrigger key={index} value={stage.name} count={meta.count_per_stage?.[stage.name]}>
													{stage.label}
												</TabsTrigger>
											</>
										);
									}
									return (
										<TabsTrigger key={index} value={stage.name} count={meta.count_per_stage?.[stage.name]}>
											{stage.label === "Post Judgment" ? "Judgment / Post Judgment" : stage.label}
										</TabsTrigger>
									);
								})}

							<TabsTrigger key="mediation" value={"mediation"} count={meta.count_has_mediation}>
								Under Mediation
							</TabsTrigger>
						</TabsList>
						<div className="flex flex-col w-full gap-4 mt-4">
							<Searchbar
								defaultValue={search.search}
								onChange={async (value) => {
									await navigate({
										to: "/case-evaluation",
										search: (old) => ({
											...old,
											search: value,
											page: 1,
										}),
									});
								}}
							/>
							{cases.length > 0 ? (
								<>
									<DataTable
										className="border-collapse"
										data={cases}
										columns={columns}
										stickyHead={false}
										stickyCol={false}
										showBorder={true}
										compact={true}
										showToolbar={false}
									/>
									<Pagination
										onClick={async (page) => {
											await navigate({
												to: "/case-evaluation",
												search: (old) => ({
													...old,
													page,
												}),
											});
										}}
										initialPage={meta.current_page}
										pageCount={meta.last_page}
										itemsPerPage={search.per_page || 10}
										onItemsPerPageChange={async (value) => {
											await navigate({
												to: "/case-evaluation",
												search: (old) => ({
													...old,
													per_page: value,
													page: 1,
												}),
											});
										}}
										itemsPerPageOptions={[10, 20, 50, 100]}
									/>
								</>
							) : (
								<Blanket width="sm">
									<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
									<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
								</Blanket>
							)}
						</div>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}

function ReportModal() {
	const modal = useModal();
	const [isGenerating, setIsGenerating] = useState(false);
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [searchTerm, setSearchTerm] = useState("");

	const reportSchema = z.object({
		date_from: z.string().optional(),
		date_to: z.string().optional(),
		case_types: z.array(z.string()).optional(),
	});

	const form = useForm<ReportFormValues>({
		resolver: zodResolver(reportSchema),
		defaultValues: {
			date_from: "",
			date_to: "",
			case_types: [],
		},
	});

	const From = form.watch("date_from");
	const To = form.watch("date_to");

	const caseTypes = useSuspenseQuery(caseTypesQueryOptions({ search: searchTerm }));
	const documents = caseTypes.data.data;
	const { closeModal } = useModalControl();
	const handleGenerateReport = async () => {
		try {
			setIsGenerating(true);

			const data = {
				date_from: form.getValues("date_from") || undefined,
				date_to: form.getValues("date_to") || undefined,
				format: "pdf",
				case_types: form.getValues("case_types"),
			};

			const response = await apiClient.post("reports/case-record-list", data, {
				responseType: "blob",
			});

			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `case_record_list_${new Date().toISOString().split("T")[0]}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			toast.success("Report generated successfully");
			modal.closeModal();
		} catch (error) {
			console.error("Error generating report:", error);
			if (error.response?.data) {
				try {
					const reader = new FileReader();
					reader.onload = () => {
						try {
							const errData = JSON.parse(reader.result as string);
							if (errData.message) {
								toast.error(errData.message);
							} else {
								toast.error("Failed to generate report");
							}
						} catch {
							toast.error("Failed to generate report");
						}
					};
					reader.readAsText(error.response.data);
				} catch {
					toast.error("Failed to generate report");
				}
			} else {
				toast.error("Failed to generate report");
			}
		} finally {
			setIsGenerating(false);
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleGenerateReport)} className="space-y-6">
				<p className="text-sm text-foreground/70">Select a date range to filter the cases for the report. Leave empty to include all cases.</p>

				<div className="space-y-4">
					<FormField
						control={form.control}
						name="date_from"
						render={({ field }) => (
							<FormItem>
								<FormLabel>From Date</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="date_to"
						render={({ field }) => (
							<FormItem>
								<FormLabel>To Date</FormLabel>
								<FormControl>
									<Input type="date" min={form.getValues("date_from")} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="case_types"
						render={({ field }) => (
							<FormItem>
								<div className="flex flex-col w-full">
									<FormLabel>Case Types</FormLabel>
								</div>
								<FormGroup>
									<TagsInput
										isMulti
										value={documents.filter((doc) => field.value?.includes(doc.id)).map((doc) => ({ label: doc.name, value: doc.id }))}
										onValueChange={(opts) => field.onChange(opts.map((opt) => opt.value))}
										loadOptions={async (input) => {
											const res = await apiClient.get("/case-types", {
												params: {
													"filter[search]": input,
												},
											});
											return res.data.data.map((item: { id: string; name: string }) => ({
												label: item.name,
												value: item.id,
											}));
										}}
									/>
								</FormGroup>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="flex gap-2 justify-end">
					<Button variant="outline" type="button" onClick={() => closeModal()} disabled={isGenerating}>
						Cancel
					</Button>
					<Button type="submit" disabled={isGenerating || !From || !To}>
						{isGenerating ? (
							<>
								<span className="animate-spin mr-2">⏳</span>
								Generating...
							</>
						) : (
							<>
								<IconFileDownload size={18} stroke={2} className="mr-2" />
								Generate Report
							</>
						)}
					</Button>
				</div>
			</form>
		</Form>
	);
}
